/*
  Dashboard layout after successful login
*/

import { ReactComponent as TWMLogo } from '@/assets/logo_twm.svg'
import { ReactComponent as TWMLogoSuffix } from '@/assets/logo_twm_suffix.svg'
import PersonIcon from '@mui/icons-material/Person'
import SettingsIcon from '@mui/icons-material/Settings'
import AppBar from '@mui/material/AppBar'
import Box from '@mui/material/Box'
import Breadcrumbs from '@mui/material/Breadcrumbs'
import Button from '@mui/material/Button'
import Container from '@mui/material/Container'
import CssBaseline from '@mui/material/CssBaseline'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import Link from '@mui/material/Link'
import Menu from '@mui/material/Menu'
import useMediaQuery from '@mui/material/useMediaQuery'

import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import Toolbar from '@mui/material/Toolbar'
import { Outlet, matchRoutes, useNavigate } from 'react-router-dom'

import DashboardRedirect from '@/components/DashboardRedirect'
import useHistoryStack from '@/hooks/useHistoryStack'
import { getPaths } from '@/routes'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import get from 'lodash/get'
import last from 'lodash/last'
import startsWith from 'lodash/startsWith'
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { connect, useSelector } from 'react-redux'
import usePermissions from '@/hooks/usePermissions'
import useIsReportOnlyRole from '@/hooks/useIsReportOnlyRole'
import PermissionsController from '@/components/PermissionsController'

const mapStateToProps = ({ app }) => ({
  scrollTo: app.scrollTo,
  scrollBy: app.scrollBy,
})

const navItemSx = (highlighted = false) => ({
  '&.MuiMenuItem-root': {
    fontWeight: highlighted ? 'bold' : 'normal',
    color: theme => {
      if (highlighted) {
        return theme.palette.primary.main
      }

      return theme.palette.other.black.main
    },

    '&:hover': {
      color: 'common.white',
      background: theme => theme.palette.primary.main,
    },
  },
})

const getMenuOptionsPath = (options = []) => {
  const paths = []

  options.forEach(option => {
    const subMenuOptions = get(option, 'menuOptions')
    if (subMenuOptions) {
      paths.push(...getMenuOptionsPath(subMenuOptions))
    } else {
      const pathname = get(option, 'pathname')

      if (pathname) paths.push(pathname)
    }
  })

  return paths
}

const NavMenuItem = ({ label, pathname, menuOptions, onNavigate }) => {
  const navigate = useNavigate()
  const [menuOpen, setMenuOpen] = useState(false)
  const openMenuRef = useRef()

  const timerRef = useRef()
  const isMouseInMenu = useRef()

  const menuRefCallback = useCallback(node => {
    if (node !== null) {
      setMenuOpen(node.open)
      openMenuRef.current = node.setAnchorEl
    }
  }, [])

  const historyStack = useHistoryStack()
  const pathList = [pathname, ...getMenuOptionsPath(menuOptions)]

  const isSelected = pathList.includes(
    historyStack
      ?.map(({ pathname: p }) => p)
      ?.slice(-1)
      .pop(0),
  )

  const startTimer = () => {
    timerRef.current = setTimeout(() => {
      if (!isMouseInMenu.current) {
        openMenuRef.current?.()
      }
    }, 200)
  }

  useEffect(
    () => () => {
      clearTimeout(timerRef.current)
    },
    [],
  )

  return (
    <>
      <MenuItem
        sx={navItemSx(isSelected || menuOpen)}
        onMouseEnter={e => {
          if (menuOptions) {
            openMenuRef.current?.(e.currentTarget)
          }
        }}
        onMouseLeave={() => {
          startTimer()
        }}
        onClick={e => {
          if (menuOptions) {
            openMenuRef.current?.(e.currentTarget)
          } else {
            onNavigate?.()
            navigate(pathname, { replace: true })
          }
        }}
      >
        {label}
        {menuOptions && <KeyboardArrowRightIcon sx={{ ml: 1 }} />}
      </MenuItem>
      {menuOptions && (
        <NavMenu
          ref={menuRefCallback}
          menuOptions={menuOptions}
          onNavigateClose={() => {
            onNavigate?.()
          }}
          setIsInMenu={val => {
            isMouseInMenu.current = val
          }}
        />
      )}
    </>
  )
}

const NavMenu = forwardRef(
  (
    {
      setIsInMenu,
      menuListProps,
      menuOptions,
      menuProps: { sx, ...menuProps } = {},
      onNavigateClose,
    },
    ref,
  ) => {
    const [anchorEl, setAnchorEl] = useState()

    useImperativeHandle(ref, () => ({
      setAnchorEl: el => {
        setAnchorEl(el)
      },
      open: !!anchorEl,
    }))

    useEffect(() => {
      if (!anchorEl) {
        setIsInMenu?.()
      }
    }, [anchorEl, setIsInMenu])

    return (
      <Menu
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={() => {
          setAnchorEl(null)
        }}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        sx={{
          mt: -1,
          '&.MuiPopover-root': { pointerEvents: 'none' },
          '& .MuiPaper-root': { pointerEvents: 'auto' },
          ...sx,
        }}
        MenuListProps={{
          sx: { py: 0 },
          onMouseLeave: () => {
            setIsInMenu?.()
            setAnchorEl()
          },
          onMouseEnter: () => {
            setIsInMenu?.(true)
          },
          ...menuListProps,
        }}
        {...menuProps}
      >
        {menuOptions
          .filter(({ hideInMenu }) => !hideInMenu)
          .map((option, index) => (
            <NavMenuItem
              key={`navmenuitem_${index * 2}`}
              {...option}
              onNavigate={() => {
                setAnchorEl(null)
                onNavigateClose?.()
              }}
            />
          ))}
      </Menu>
    )
  },
)

const NavButton = ({ pathname, label, menuOptions, preserveHistory }) => {
  const navigate = useNavigate()
  const historyStack = useHistoryStack()
  const [menuOpen, setMenuOpen] = useState(false)
  const openMenuRef = useRef()
  const timerRef = useRef()
  const isMouseInMenu = useRef()

  const menuRefCallback = useCallback(node => {
    if (node !== null) {
      setMenuOpen(node.open)
      openMenuRef.current = node.setAnchorEl
    }
  }, [])

  const pathList = [pathname, ...getMenuOptionsPath(menuOptions)]
  const currentPath = last(historyStack.map(({ pathname: p }) => p))

  const isSelected = pathList.some(path => startsWith(currentPath, path))

  // generates a media query string that targets screens <= md
  // const isSmallScreen = useMediaQuery(theme => theme.breakpoints.down('md'));
  const isSmallScreen = useMediaQuery('(max-width: 920px)')
  const boxPx = isSmallScreen ? 0 : 4

  const startTimer = () => {
    timerRef.current = setTimeout(() => {
      if (!isMouseInMenu.current) {
        openMenuRef.current?.()
      }
    }, 200)
  }

  useEffect(
    () => () => {
      clearTimeout(timerRef.current)
    },
    [],
  )

  return (
    <Box px={boxPx}>
      <Button
        onMouseEnter={e => {
          if (menuOptions) {
            openMenuRef.current?.(e.currentTarget)
          }
        }}
        onMouseLeave={() => {
          startTimer()
        }}
        onClick={e => {
          if (menuOptions) {
            openMenuRef.current?.(e.currentTarget)
          } else if (preserveHistory && startsWith(currentPath, pathname)) {
            // 已在此選單項目對應的路由範圍內（例如成績單子路由）時，導覽但不重置麵包屑/返回鍵的
            // historyStack，避免使用者在子頁面誤點同一選單項目導致返回鍵消失
            // 見 useHistoryStack.js：REPLACE 型別導覽預設會將 stack 重置為長度 1
            navigate(pathname, { replace: true, state: { reset: false } })
          } else {
            navigate(pathname, { replace: true })
          }
        }}
        size="small"
        sx={{
          fontWeight: isSelected ? 'bold' : 'normal',
          color: theme => {
            if (isSelected || menuOpen) {
              return theme.palette.primary.main
            }

            return theme.palette.other.black.main
          },
          borderRadius: 10,
          '&:hover': {
            color: theme => theme.palette.primary.main,
          },

          fontSize: 15,
        }}
      >
        {label}
      </Button>

      {menuOptions && (
        <NavMenu
          ref={menuRefCallback}
          menuOptions={menuOptions}
          menuProps={{
            anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
            transformOrigin: { vertical: 'top', horizontal: 'center' },
            sx: { mt: 1 },
          }}
          setIsInMenu={val => {
            isMouseInMenu.current = val
          }}
        />
      )}
    </Box>
  )
}

const DashboardLayout = ({ dispatch, scrollTo, scrollBy }) => {
  const navigate = useNavigate()
  const { t, i18n } = useTranslation()

  const historyStack = useHistoryStack()
  const contentContainerRef = useRef()
  const [profileAnchor, setProfileAnchor] = useState()
  const [settingsAnchor, setSettingsAnchor] = useState()

  const currentPath = last(historyStack.map(({ pathname: p }) => p))

  const profileSelected = startsWith(currentPath, '/personal_details')
  const settingsSelected = startsWith(currentPath, '/manage_accounts')
  const domainSelected = startsWith(currentPath, '/verify_domain')

  const canViewUser = usePermissions('user_view')
  const canViewUserGroup = usePermissions('userGroup_view')
  const canViewEmailCampaign = usePermissions('emailCampaign_view')
  const canViewSmsCampaign = usePermissions('smsCampaign_view')
  const canViewEmailTemplateSystem = usePermissions('emailTempSystem_view')
  const canViewEmailTemplatePerson = usePermissions('emailTempPerson_view')
  const canViewSmsTemplateSystem = usePermissions('smsTempSystem_view')
  const canViewSmsTemplatePerson = usePermissions('smsTempPerson_view')
  const canViewLandingPageSystem = usePermissions('landingpageSystem_view')
  const canViewLandingPagePerson = usePermissions('landingpagePerson_view')
  const canViewTrainingCampaign = usePermissions('courseTrainingEvent_view')
  const canViewCourse = usePermissions('courseVideo_view')
  const canViewMyVideos = usePermissions('courseVideosList_view')
  const canViewPhishReport = usePermissions('phishReport_view')
  const canViewCourseReport = usePermissions('courseReport_view')

  const isReportOnlyRole = useIsReportOnlyRole()
  const companyId = useSelector(state => state.auth.account?.companyId)

  useEffect(() => {
    if (scrollBy) {
      const scrollRef = contentContainerRef.current
      if (scrollRef) {
        scrollRef.scrollTop += scrollBy
      }

      setTimeout(() => {
        dispatch({ type: 'app/scrollBy' })
      }, 300)
    }
  }, [scrollBy, dispatch])

  useEffect(() => {
    if (scrollTo) {
      const scrollRef = contentContainerRef.current
      if (scrollRef) {
        scrollRef.scrollTop = scrollTo
      }

      setTimeout(() => {
        dispatch({ type: 'app/scrollTo' })
      }, 300)
    }
  }, [scrollTo, dispatch])

  return (
    <>
      <DashboardRedirect />
      <Box
        sx={{
          display: 'flex',
          height: '100vh',
          background: theme => theme.palette.white.main,
        }}
      >
        <CssBaseline />
        <AppBar
          color=""
          elevation={0}
          sx={{
            justifyContent: 'center',
          }}
        >
          <Box
            sx={{
              position: 'relative',
              alignItems: 'center',
              display: 'flex',
            }}
          >
            <Toolbar sx={{ px: 5, pt: 5 }}>
              <TWMLogo />
              <Divider
                orientation="vertical"
                flexItem
                sx={{
                  mx: 2,
                  my: 1,
                  borderColor: theme => theme.palette.secondary_80.main,
                  borderRightWidth: '2px',
                }}
              />
              <TWMLogoSuffix />
            </Toolbar>
            <Box sx={{ position: 'absolute', right: 20, pt: 5 }}>
              {/* <Button
              disableRipple
              color="primary"
              sx={{ background: 'transparent !important' }}
              onClick={() => {
                i18n.changeLanguage(isEqual(i18n.language, 'enUS') ? 'zhTW' : 'enUS')
              }}
            >
              {t('general.language')}
            </Button> */}

              {/* <Button
                disableRipple
                size="small"
                color="primary"
                variant="contained"
                sx={{ borderRadius: 4 }}
                onClick={() => {
                  dispatch({ type: 'auth/logout' })
                }}
              >
                {t('general.logout')}
              </Button> */}
              <PermissionsController permission="account_create">
                <IconButton
                  color={
                    settingsSelected || domainSelected || settingsAnchor
                      ? 'primary'
                      : 'secondary_80'
                  }
                  onClick={e => {
                    setSettingsAnchor(e.currentTarget)
                  }}
                >
                  <SettingsIcon />
                </IconButton>
              </PermissionsController>

              {!isReportOnlyRole && (
                <IconButton
                  color={profileSelected || profileAnchor ? 'primary' : 'secondary_80'}
                  onClick={e => {
                    setProfileAnchor(e.currentTarget)
                  }}
                >
                  <PersonIcon />
                </IconButton>
              )}
            </Box>
          </Box>
          <Toolbar
            sx={{
              justifyContent: isReportOnlyRole ? 'center' : 'flex-end',
              gap: isReportOnlyRole ? 4 : 0,
              borderBottom: theme => `1px solid ${theme.palette.divider}`,
            }}
          >
            {!isReportOnlyRole && <NavButton pathname="/home" label={t('menu.home')} />}
            {!isReportOnlyRole && (
              <NavButton
                label={t('menu.template')}
                menuOptions={[
                  {
                    hideInMenu: !canViewEmailTemplateSystem && !canViewEmailTemplatePerson,
                    label: t('menu.phish_email'),
                    menuOptions: [
                      {
                        hideInMenu: !canViewEmailTemplateSystem,
                        label: t('menu.system'),
                        pathname: '/phish_template_email/system',
                      },
                      {
                        hideInMenu: !canViewEmailTemplatePerson,
                        label: t('menu.individual'),
                        pathname: '/phish_template_email/individual',
                      },

                      { hideInMenu: true, pathname: '/phish_template_email/system/create' },
                      { hideInMenu: true, pathname: '/phish_template_email/individual/create' },
                      { hideInMenu: true, pathname: '/phish_template_email/edit' },
                    ],
                  },
                  {
                    hideInMenu: !canViewLandingPageSystem && !canViewLandingPagePerson,
                    label: t('menu.phish_web'),
                    menuOptions: [
                      {
                        hideInMenu: !canViewLandingPageSystem,
                        label: t('menu.system'),
                        pathname: '/phish_template_landing/system',
                      },
                      {
                        hideInMenu: !canViewLandingPagePerson,
                        label: t('menu.individual'),
                        pathname: '/phish_template_landing/individual',
                      },
                      // {
                      //   label: t('menu.test_mobile'),
                      //   pathname: '/phish_template_landing/individual/mobile_create',
                      // },

                      { hideInMenu: true, pathname: '/phish_template_landing/system/create' },
                      { hideInMenu: true, pathname: '/phish_template_landing/individual/create' },
                      { hideInMenu: true, pathname: '/phish_template_landing/edit' },
                    ],
                  },
                  {
                    hideInMenu:
                      companyId !== 1 || (!canViewSmsTemplateSystem && !canViewSmsTemplatePerson),
                    label: t('menu.phish_sms'),
                    menuOptions: [
                      {
                        hideInMenu: !canViewSmsTemplateSystem,
                        label: t('menu.system'),
                        pathname: '/phish_template_sms/system',
                      },
                      {
                        hideInMenu: !canViewSmsTemplatePerson,
                        label: t('menu.individual'),
                        pathname: '/phish_template_sms/individual',
                      },

                      { hideInMenu: true, pathname: '/phish_template_sms/system/create' },
                      { hideInMenu: true, pathname: '/phish_template_sms/individual/create' },
                      { hideInMenu: true, pathname: '/phish_template_sms/edit' },
                    ],
                  },
                ]}
              />
            )}
            {!isReportOnlyRole && (
              <NavButton
                pathname="/phish_campaign"
                label={t('menu.phish_campaign')}
                menuOptions={[
                  {
                    hideInMenu: !canViewEmailCampaign,
                    label: t('menu.phish_campaign_email'),
                    pathname: '/phish_campaign_email',
                  },
                  {
                    hideInMenu: companyId !== 1 || !canViewSmsCampaign,
                    label: t('menu.phish_campaign_sms'),
                    pathname: '/phish_campaign_sms',
                  },
                ]}
              />
            )}

            {/* only uat */}
            {!isReportOnlyRole && companyId === 1 && (
              <NavButton
                pathname="/campaign"
                label={t('menu.training')}
                menuOptions={[
                  {
                    hideInMenu: !canViewTrainingCampaign,
                    label: t('menu.training_activity'),
                    pathname: '/campaign',
                  },
                  {
                    hideInMenu: !canViewCourse,
                    label: t('menu.campaign_course_management'),
                    pathname: '/campaign_course_management',
                  },
                  {
                    hideInMenu: !canViewMyVideos,
                    label: t('menu.my_videos'),
                    pathname: '/my_videos',
                  },
                ]}
              />
            )}

            {isReportOnlyRole ? (
              <>
                <NavButton
                  pathname="/user_management"
                  label={t('menu.user_management')}
                  menuOptions={[
                    {
                      hideInMenu: !canViewUser,
                      label: t('menu.registered_user_management'),
                      pathname: '/user_management',
                    },
                    {
                      hideInMenu: !canViewUserGroup,
                      label: t('menu.group_management'),
                      pathname: '/group_management',
                    },
                  ]}
                />
                <NavButton
                  pathname="/report"
                  label={t('menu.report')}
                  menuOptions={[
                    {
                      hideInMenu: !canViewPhishReport,
                      label: t('menu.phish_report'),
                      pathname: '/phish_report',
                    },
                  ]}
                />
                <IconButton
                  sx={{ ml: 4 }}
                  color={profileSelected || profileAnchor ? 'primary' : 'secondary_80'}
                  onClick={e => {
                    setProfileAnchor(e.currentTarget)
                  }}
                >
                  <PersonIcon />
                </IconButton>
              </>
            ) : (
              <>
                <NavButton
                  pathname="/report"
                  label={t('menu.report')}
                  menuOptions={[
                    {
                      hideInMenu: !canViewPhishReport,
                      label: t('menu.phish_report'),
                      pathname: '/phish_report',
                    },
                    // {
                    //   hideInMenu: !canViewCourseReport,
                    //   label: t('menu.course_report'),
                    //   pathname: '/course_report',
                    // },
                  ]}
                />
                <NavButton pathname="/transcript" label={t('menu.transcript')} preserveHistory />
                <NavButton
                  pathname="/user_management"
                  label={t('menu.user_management')}
                  menuOptions={[
                    {
                      hideInMenu: !canViewUser,
                      label: t('menu.registered_user_management'),
                      pathname: '/user_management',
                    },
                    {
                      hideInMenu: !canViewUserGroup,
                      label: t('menu.group_management'),
                      pathname: '/group_management',
                    },
                  ]}
                />
              </>
            )}
          </Toolbar>
        </AppBar>
        <Box
          component="main"
          sx={{
            pt: 0.5,
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Toolbar />
          <Toolbar />
          <Container
            ref={contentContainerRef}
            maxWidth={false}
            sx={{
              flex: 1,
              mt: 2,
              pt: 2,
              pb: 4,
              mx: 0,
              overflow: 'auto',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Stack id="layout-breadcrumb-bar" direction="row" px={2} mb={2} pt={1}>
              <Breadcrumbs
                separator=">"
                sx={{
                  '& .MuiBreadcrumbs-separator': {
                    fontWeight: 'bold',
                    color: theme => theme.palette.primary.main,
                  },
                }}
              >
                {historyStack.map(({ pathname: p, state }, index) => {
                  const route = matchRoutes(getPaths(), { pathname: p })
                  const routePattern = get(route, '[0].route.path', '')
                  const routePath = get(route, '[0].pathname', '')

                  let translationPath
                  const patternTranslation = `breadcrumbs.${routePattern
                    .replaceAll('/', '_')
                    .replaceAll(':', '')}`

                  const pathTranslation = `breadcrumbs.${routePath
                    .replaceAll('/', '_')
                    .replaceAll(':', '')}`

                  const patternTranslationExist = i18n.exists(patternTranslation)
                  const pathTranslationExist = i18n.exists(pathTranslation)

                  if (patternTranslationExist) {
                    translationPath = patternTranslation
                  }

                  if (pathTranslationExist) {
                    translationPath = pathTranslation
                  }

                  if (translationPath) {
                    const isLast = historyStack.length - 1 === index

                    return (
                      <Link
                        component={isLast ? 'a' : 'button'}
                        key={p}
                        sx={{ fontWeight: 'bold' }}
                        underline="none"
                        onClick={() => {
                          if (!isLast) {
                            navigate(-(historyStack.length - 1 - index))
                          }
                        }}
                      >
                        {t(translationPath, state)}
                      </Link>
                    )
                  }
                  return null
                })}
              </Breadcrumbs>
              <Box flex={1} />
              {historyStack.length > 1 && (
                <Button
                  onClick={() => {
                    navigate(-1)
                  }}
                >
                  {t('general.back')}
                </Button>
              )}
            </Stack>
            <Outlet />

            <Menu
              anchorEl={profileAnchor}
              open={!!profileAnchor}
              onClose={() => {
                setProfileAnchor(null)
              }}
              MenuListProps={{
                sx: { py: 0 },
              }}
            >
              <MenuItem
                sx={navItemSx(profileSelected)}
                onClick={() => {
                  navigate('personal_details', { replace: true })
                  setProfileAnchor(null)
                }}
              >
                {t('general.personal_details')}
              </MenuItem>

              <MenuItem
                sx={navItemSx()}
                onClick={() => {
                  dispatch({ type: 'auth/logout' })
                }}
              >
                {t('general.logout')}
              </MenuItem>
            </Menu>

            <Menu
              anchorEl={settingsAnchor}
              open={!!settingsAnchor}
              onClose={() => {
                setSettingsAnchor(null)
              }}
              MenuListProps={{
                sx: { py: 0 },
              }}
            >
              <PermissionsController permission="account_create">
                <MenuItem
                  sx={navItemSx(settingsSelected)}
                  onClick={() => {
                    navigate('manage_accounts', { replace: true })
                    setSettingsAnchor(null)
                  }}
                >
                  {t('general.manage_accounts')}
                </MenuItem>
              </PermissionsController>

              <PermissionsController permission="account_create">
                <MenuItem
                  sx={navItemSx(domainSelected)}
                  onClick={() => {
                    navigate('verify_domain', { replace: true })
                    setSettingsAnchor(null)
                  }}
                >
                  {t('general.verify_domain')}
                </MenuItem>
              </PermissionsController>
            </Menu>
          </Container>
        </Box>
      </Box>
    </>
  )
}

export default connect(mapStateToProps)(DashboardLayout)
